import { useCallback, useMemo } from 'react';
import { concat, filter, isNonNullish, isNullish, join, pipe, unique } from 'remeda';
import { PartnerSetting, useManagePartnerSettingsValue, usePartnerSetting } from 'src/graphql';
import { SignalUrgencyLevel } from 'src/graphql/entities/signal-business-hours/signal-business-hours.types';
import { useGlobalStore } from 'src/store';
import { toEnum } from 'src/utils/enum';

export const useManageUrgencyLevel = (): [
  SignalUrgencyLevel[],
  (urgencyLevel: SignalUrgencyLevel, disable: boolean) => Promise<void>,
] => {
  const partnerId = useGlobalStore.use.selectedPartnerId();

  const { updatePartnerSettingsValue, createPartnerSettingsValue } =
    useManagePartnerSettingsValue(partnerId);

  const { data: disabledConversationUrgencyLevels } = usePartnerSetting(
    PartnerSetting.DisabledConversationUrgencyLevels,
  );

  const disabledUrgencyLevels = useMemo<SignalUrgencyLevel[]>(
    () =>
      (disabledConversationUrgencyLevels?.value?.split(',') || [])
        .map((value) => toEnum(SignalUrgencyLevel, value))
        .filter<SignalUrgencyLevel>(isNonNullish),
    [disabledConversationUrgencyLevels],
  );

  const setDisabledUrgencyLevels = useCallback(
    async (urgencyLevel: SignalUrgencyLevel, disable: boolean): Promise<void> => {
      if (isNullish(partnerId)) return;

      const partnerSettingId =
        disabledConversationUrgencyLevels?.partnerSettingId ||
        PartnerSetting.DisabledConversationUrgencyLevels;

      const value = pipe(
        disabledUrgencyLevels,
        concat([urgencyLevel]),
        filter((val) => val !== urgencyLevel || (val === urgencyLevel && disable)),
        unique(),
        join(','),
      );

      // case when both current and platform partners don't have DisabledConversationUrgencyLevels setting value defined.
      if (!disabledConversationUrgencyLevels) {
        await createPartnerSettingsValue({
          partnerSettingId,
          value,
        });
        return;
      }

      // case when setting value is define for current partner and needs to be updated.
      if (
        isNonNullish(disabledConversationUrgencyLevels.id) &&
        disabledConversationUrgencyLevels.partnerId === partnerId
      ) {
        await updatePartnerSettingsValue({
          id: disabledConversationUrgencyLevels.id,
          partnerSettingValueId: disabledConversationUrgencyLevels.partnerSettingValueId,
          value,
        });
        return;
      }

      // case when it is platform partner setting and partner creates own setting.
      if (disabledConversationUrgencyLevels.partnerId !== partnerId) {
        await createPartnerSettingsValue({
          partnerSettingValueId: disabledConversationUrgencyLevels.partnerSettingValueId,
          value,
        });
        return;
      }
    },
    [
      createPartnerSettingsValue,
      disabledConversationUrgencyLevels,
      disabledUrgencyLevels,
      partnerId,
      updatePartnerSettingsValue,
    ],
  );
  return [disabledUrgencyLevels, setDisabledUrgencyLevels];
};
